import classNames from 'classnames';
import { DateToLocal } from 'components/dates';
import { Expandable, ShowHide } from 'components/layout';
import { EditTable } from 'components/listing';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useState, useEffect, Fragment } from 'react';
import { OcrTableFormModal } from '.';
import { ActiveStatus } from 'components/status';
import { FormSelect } from 'components/form-fields';
import { useValidation } from 'hooks/useValidation';

export function OcrTableLayout(
    {
        loading = false,
        disabled = false,
        readonly = false,
        tableExpanded = false,
        tableExpandable = false,
        previsit = {},
        assetList,
        booleanNaSelectValues,
        booleanSelectValues,
        editableColumnTypes,
        employeeList,
        getColumnTypeForCell,
        layout,
        onChange,
        rowFilter
    }
) {
    const [rowBeingEdited, setRowBeingEdited] = useState(null);
    const [addingRow, setAddingRow] = useState(false);
    const [rowValidation, setRowValidation] = useState([]);
    const [isValid, , validate] = useValidation();
    const [rowIdxChanged, setRowIdxChanged] = useState(null);

    function formatRows() {
        if (!layout.table.rows) {
            return [];
        }

        return layout.table.rows
            .filter((_, index) => !hasMultipleHeaderRows() || index !== 0)
            .filter((_, index) => !rowFilter || rowFilter(_, index))
            .map((row, rowIndex) => {

                var rowValid = true;
                const foundValidValue = rowValidation.find(e => e.id === rowIndex);

                if (foundValidValue) {
                    rowValid = foundValidValue.value
                }

                return {
                    key: rowIndex,
                    cells: formatRow(row, rowIndex),
                    validity: formatRowValidity(row, rowIndex),
                    isEditable: false,
                    valid: rowValid
                };
            });
    }

    function formatRowValidity(row, rowIndex) {
        return (
            <OcrTableFormModal
                columns={layout.table.columns}
                row={row}
                getColumnTypeForCell={getColumnTypeForCell}
                employeeList={employeeList}
                assetList={assetList}
                rowBeingEdited={rowBeingEdited}
                rowIndex={rowIndex}
                onFinish={onTableFormFinish}
                onClose={onTableFormClose}
                isEditing={!addingRow}
                booleanSelectValues={booleanSelectValues}
                booleanNaSelectValues={booleanNaSelectValues}
                editableColumnTypes={editableColumnTypes}
                getEmployeeForRow={findEmployee}
                onValidationUpdate={(isValidUpdate) => { onRowValidChange(rowIndex, isValidUpdate) }}
            />
        );
    }

    function formatRow(row, rowIndex) {
        return row.map((cell, cellIndex) => {
            const columnType = getColumnTypeForCell(cellIndex);
            const employee = findEmployee(row);
            const vehicle = findVehicle(row);

            switch (columnType?.toUpperCase()) {
                case 'FIXED':
                case 'STRING':
                case 'INTEGER':
                    return formatCell(cell);
                case 'POUND':
                    return '\u00a3' + formatCell(cell);
                case 'PASSFAIL':
                    return <ActiveStatus isActive={cell === 'True'} activeText='Compliant' inactiveText='Non Compliant' />;
                case 'RATING':
                    return formatRatingCell(cell);
                case 'FIXEDDATE':
                case 'DATEPAST':
                    return <DateToLocal format='DD/MM/YYYY'>{formatDateCell(cell, 'YYYY-MM-DD')}</DateToLocal>;
                case 'DRIVERNAME':
                    return formatCell(employee?.employeeName);
                case 'VEHICLEREGISTRATION':
                    return formatCell(vehicle?.registration);
                case 'DRIVERLOCATION':
                    return formatCell(employee?.locationName);
                case 'DRIVERSTARTDATE':
                    return <DateToLocal format='DD/MM/YYYY'>{formatDateCell(employee?.startDate)}</DateToLocal>;
                case 'FIXEDTIME':
                    return <DateToLocal format='HH:mm'>{cell}</DateToLocal>;
                case 'BOOLEAN':
                    if (cell == null || cell === '') {
                        return '-';
                    } else {
                        return booleanSelectValues.find(x => x.id === cell).name;
                    }
                case 'BOOLEANNA':
                    if (cell == null || cell === '') {
                        return '-';
                    } else {
                        return booleanNaSelectValues.find(x => x.id === cell).name;
                    }
                case 'TABLEBOOLEANSELECT':
                    return formatBooleanSelect(rowIndex, cell, cellIndex, row);
                case 'HOLDINGDAYS':
                    return formatHoldingDayCell(cell);
                case 'VARIANCE':
                    return formatVarianceCell(cell);
                default:
                    return 'Column type not yet supported';
            }
        });
    }

    function formatCell(cellValue) {
        return cellValue ? cellValue : '-';
    }

    function formatRatingCell(cellValue) {
        if (previsit.usePerformanceRatings) {
            var className = 'cell-rating-' + cellValue.toLowerCase();

            return <Fragment>
                <span className={classNames(className, 'performance-rating')}>{cellValue ? cellValue : '-'}</span>
            </Fragment>;
        }

        return <span></span>;
    }

    useEffect(() => {
        if (rowIdxChanged !== null) {
            onRowValidChange(rowIdxChanged, isValid);
            setRowIdxChanged(null);
        }
    }, [rowIdxChanged]);

    function onBooleanSelectChange(value, cellIndex, rowIndex, row) {
        const rowsClone = [...layout.table.rows];
        const updatedRow = row.map(cell => cell === null ? null : cell.toString());
        updatedRow[cellIndex] = value.id;
        rowsClone[rowIndex] = updatedRow;

        const tableClone = {
            ...layout.table,
            rows: rowsClone
        };

        onChange({
            ...layout,
            table: tableClone
        });

        var rules = getRules(tableClone.columns);
        validate(rules, row);
        setRowIdxChanged(rowIndex);
    }
    function formatBooleanSelect(rowIndex, cellValue, cellIndex, row) {
        if (readonly) {
            if (cellValue == null || cellValue === '') {
                return '-';
            } else {
                return booleanSelectValues.find(x => x.id === cellValue).name;
            }
        }

        return (
            <Fragment>                
                <FormSelect
                    loading={loading}
                    onItemSelect={(value) => onBooleanSelectChange(value, cellIndex, rowIndex, row)}
                    items={booleanSelectValues}
                    selectedValue={cellValue}
                    headingText=""
                    placeholder='Choose Yes or No'
                />
            </Fragment>
        );
    }
    function formatHoldingDayCell(cellValue) {
        var className;
        var greenCutOffDays = 7;
        var days = Number(cellValue);
        if (!isNaN(days)) {
            className = 'form-field-' + ((days <= greenCutOffDays) ? 'green' : 'red');
        }

        return <Fragment>
            <span className={classNames(className)}>{cellValue ? cellValue : '-'}</span>
        </Fragment>;
    }

    function formatVarianceCell(cellValue) {
        var className;
        var value = Number(cellValue);
        if (!isNaN(value)) {

            className = 'form-field-';
            if (value < 0) {
                className = className + "red";
            } else if (value > 0) {
                className = className + "green";
            }
        }

        return <Fragment>
            <span className={classNames(className)}>{cellValue ? cellValue : '-'}</span>
        </Fragment>;
    }

    

    function formatDateCell(cellValue, inputFormat) {
        return cellValue ? moment(cellValue, inputFormat) : '-';
    }

    function findEmployee(row) {
        const employeeColumnIndex = layout.table.columns
            .findIndex(c => c.columnType.toUpperCase() === 'DRIVERNAME');

        if (employeeColumnIndex > -1) {
            const employeeId = row[employeeColumnIndex];

            return employeeList.find(e => e.employeeId === +employeeId);
        }

        return null;
    }

    function findVehicle(row) {
        const vehicleColumnIndex = layout.table.columns
            .findIndex(c => c.columnType.toUpperCase() === 'VEHICLEREGISTRATION');

        if (vehicleColumnIndex > -1) {
            const assetId = row[vehicleColumnIndex];

            return assetList.find(e => e.assetId === +assetId);
        }

        return null;
    }


    function onTableFormFinish(row) {
        const rowsClone = [...layout.table.rows];
        rowsClone[rowBeingEdited] = row.map(cell => cell === null ? null : cell.toString());

        const tableClone = {
            ...layout.table,
            rows: rowsClone
        };

        onChange({
            ...layout,
            table: tableClone
        });

        setRowBeingEdited(null);
    }

    function onTableFormClose() {
        if (addingRow) {
            onDeleteRow(rowBeingEdited);
        }

        setRowBeingEdited(null);
    }

    function formatHeaders() {
        if (hasMultipleHeaderRows()) {
            return layout.table.rows[0].map((row, rowIndex) => {
                return {
                    key: rowIndex,
                    value: row
                };
            });
        }

        return layout.table.columns.map((column, i) => {
            return {
                key: column.columnId,
                value: getColumnTypeForCell(i).toUpperCase() === 'RATING' && !previsit.usePerformanceRatings ? '' : column.columnTitle
            };
        });
    }

    function formatHeaderData() {
        return {
            headers: formatHeaders(),
            topLevelHeaders: formatTopLevelHeader(),
        }
    }

    function formatTopLevelHeader() {
        if (!hasMultipleHeaderRows()) {
            return [];
        }

        return layout.table.columns.map(column => {
            return {
                value: column.columnTitle,
                colspan: column.colspan ? column.colspan : 1
            };
        });
    }

    function hasMultipleHeaderRows() {
        return layout.table.columns.some((column) => column.colspan > 1);
    }

    function onAddRow(rowIndex) {
        const row = layout.table.columns.map(_ => '');

        const tableRowsClone = layout.table.rows ? [...layout.table.rows] : [];

        tableRowsClone.splice(rowIndex + 1, 0, row);

        const tableClone = {
            ...layout.table,
            rows: tableRowsClone
        };

        onChange({
            ...layout,
            table: tableClone
        });

        setRowBeingEdited(rowIndex + 1);
        setAddingRow(true);
    }

    function onDeleteRow(rowIndex) {
        const tableRowsClone = layout.table.rows ? [...layout.table.rows] : [];

        tableRowsClone.splice(rowIndex, 1);

        const tableClone = {
            ...layout.table,
            rows: tableRowsClone
        };

        onChange({
            ...layout,
            table: tableClone
        });
    }

    function onEditRow(rowIndex) {
        setRowBeingEdited(rowIndex);
        setAddingRow(false);
    }

    function anyEditableRows() {
        return layout.table.columns
            .filter(x => editableColumnTypes.includes(x.columnType.toUpperCase()))
            .length > 0;
    }

    function canEditRows() {
        return layout.table.canAddRows || anyEditableRows();
    }

    function onRowValidChange(rowIndex, isValidRow) {
        setRowValidation(function (prevState) {

            var clonedArray = [
                ...prevState
            ];

            const foundValue = clonedArray.find(e => e.id === rowIndex);

            if (foundValue) {
                foundValue.value = isValidRow;
            } else {
                clonedArray.push({ id: rowIndex, value: isValidRow });
            }

            return clonedArray;
        });
    }

    function getRules(cols) {
        if (cols) {
            return cols
                .map((_, cellIndex) => {
                    return { fieldName: cellIndex.toString(), required: cellIsEditable(cellIndex) && !cellIsOptional(cellIndex, cols) };
                });
        }

        return [];
    }

    function cellIsEditable(cellIndex) {
        const columnType = getColumnTypeForCell(cellIndex).toUpperCase();

        return editableColumnTypes.includes(columnType);
    }

    function cellIsOptional(cellIndex, cols) {
        return cols[cellIndex].optional;
    }

    function renderTable() {
        return (
            <EditTable
                headerData={formatHeaderData()}
                data={formatRows()}
                loadingData={loading}
                noDataMessage={layout.table.emptyMessage}
                noDataHeading={null}
                canAddRows={layout.table.canAddRows && !readonly}
                onAddRowClick={onAddRow}
                canDeleteRows={layout.table.canAddRows && !readonly}
                onDeleteRowClick={onDeleteRow}
                canEditAllRows={canEditRows() && !readonly}
                onEditRowClick={onEditRow}
                disabled={disabled || loading || readonly}
                fitToContent={true}
            />
        );
    }

    return (
        <div className='spacer-bottom'>
            <ShowHide
                evaluator={!!layout.secondaryLayoutTitle}
                show={<h4>{layout.secondaryLayoutTitle}</h4>}
            />
            <ShowHide
                evaluator={tableExpandable}
                show={
                    <Expandable
                        loading={loading}
                        expanded={tableExpanded}
                    >
                        {renderTable()}
                    </Expandable>
                }
                hide={renderTable()}
            />
        </div>
    );
}

OcrTableLayout.propTypes = {
    loading: PropTypes.bool,
    layout: PropTypes.object.isRequired,
    employeeList: PropTypes.array.isRequired,
    assetList: PropTypes.array.isRequired,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    booleanSelectValues: PropTypes.array.isRequired,
    booleanNaSelectValues: PropTypes.array.isRequired,
    editableColumnTypes: PropTypes.array.isRequired,
    getColumnTypeForCell: PropTypes.func.isRequired,
    readonly: PropTypes.bool,
    tableExpanded: PropTypes.bool,
    tableExpandable: PropTypes.bool,
    previsit: PropTypes.object,
    rowFilter: PropTypes.func
};
